import React from "react";
import { TableBody } from "@mui/material";
import {
  formatTableData,
  sortRows,
  formatCombinedTableData,
  sortCombinedRows,
  TableSegment,
} from "../../utils/segmentationTableUtils";
import { AnalysisDriverType } from "../../types/types";
import { SegmentResponse } from "@/graphql/types/graphql";
import EmptyTableBody from "./EmptyTableBody";
import SkeletonHeader from "./SkeletonHeader";
import SegmentationTableBodyRows from "./SegmentationTableBodyRows";

interface SegmentationTableBodyProps {
  /** Array of table segments */
  segments: TableSegment[];
  /** Segment response data */
  data: SegmentResponse[] | undefined;
  /** Mode for driver 1 metrics */
  driver1Mode: AnalysisDriverType;
  /** Mode for driver 2 metrics */
  driver2Mode: AnalysisDriverType;
  /** Column to sort by */
  sortColumn: string;
  /** Sort direction */
  sortDirection: "asc" | "desc";
  /** Indicates if there is one data and one manual segment */
  hasOneDataAndOneManualSegment: boolean;
  /** Indicates if skeleton loading state should be displayed */
  isSkeleton: boolean;
}

const SegmentationTableBody: React.FC<SegmentationTableBodyProps> = ({
  segments,
  data,
  driver1Mode,
  driver2Mode,
  sortColumn,
  sortDirection,
  hasOneDataAndOneManualSegment,
  isSkeleton,
}) => {
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isSkeleton) {
    const skeletonColumnCount = isManualOnly || isDataOnly || hasOneDataAndOneManualSegment ? 13 : 15;
    return <SkeletonHeader columnCount={skeletonColumnCount} />;
  }

  if (isManualOnly || isDataOnly) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatTableData(segments, data);
    const sortedRows = sortRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return (
      <TableBody>
        {displaySegments.length > 0 ? (
          <SegmentationTableBodyRows
            rows={sortedRows}
            segments={segments}
            driver1Mode={driver1Mode}
            driver2Mode={driver2Mode}
            isManualOnly={isManualOnly}
            isDataOnly={isDataOnly}
          />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatCombinedTableData(segments, data);
    const sortedRows = sortCombinedRows(combinedRows, sortColumn, sortDirection);
    return (
      <TableBody>
        {sortedRows.length > 0 ? (
          <SegmentationTableBodyRows
            rows={sortedRows}
            driver1Mode={driver1Mode}
            driver2Mode={driver2Mode}
            hasOneDataAndOneManualSegment
          />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  if (isTwoSameMethod) {
    const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatTableData(segments, data);
    const sortedRows = sortRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
    return (
      <TableBody>
        {displaySegments.length > 0 ? (
          <SegmentationTableBodyRows
            rows={sortedRows}
            segments={segments}
            driver1Mode={driver1Mode}
            driver2Mode={driver2Mode}
            isTwoSameMethod
          />
        ) : (
          <EmptyTableBody colSpan={15} />
        )}
      </TableBody>
    );
  }

  const { displaySegments, rows, isDataOnly: isDataOnlyFromFormat } = formatTableData(segments, data);
  const sortedRows = sortRows(rows, sortColumn, sortDirection, isDataOnlyFromFormat);
  return (
    <TableBody>
      {displaySegments.length > 0 ? (
        <SegmentationTableBodyRows
          rows={sortedRows}
          segments={segments}
          driver1Mode={driver1Mode}
          driver2Mode={driver2Mode}
        />
      ) : (
        <EmptyTableBody colSpan={15} />
      )}
    </TableBody>
  );
};

export default SegmentationTableBody;
